import { useCallback, useEffect, useMemo, useState } from "react";
import { BookOpen, CheckCircle2, Headphones, Mail, MessageSquarePlus, Phone, Search, Ticket, X } from "lucide-react";
import { api } from "../../services/api";
import { formatUsDateTime } from "../../utils/timezone";
import { InsightCard, PaginationControls, StatusPill, WorkflowToolbar } from "./AdminPanelParts";
import { useAdminResource } from "./useAdminResource";
import { usePagination } from "./usePagination";
import { ToastStack, useToast } from "./useToast";

const fallbackPayload = {
  resources: [],
  metrics: { openTickets: 0, openInquiries: 0, guides: 0, tutorials: 0 },
};

const statusOptions = ["open", "in_progress", "resolved", "archived"];
const priorityOptions = ["low", "medium", "high", "urgent"];
const areaOptions = ["all", "contact", "general", "products", "orders", "shipping", "sync", "billing", "returns", "reports", "setup"];

function statusTone(status) {
  if (["published", "resolved"].includes(status)) return "good";
  if (["open", "in_progress"].includes(status)) return "warn";
  if (status === "archived") return "neutral";
  return "info";
}

function priorityTone(priority) {
  if (priority === "urgent") return "bad";
  if (priority === "high") return "warn";
  if (priority === "low") return "neutral";
  return "info";
}

function kindLabel(kind) {
  if (kind === "inquiry") return "Contact inquiry";
  if (kind === "ticket") return "Support request";
  if (kind === "doc") return "Internal doc";
  return kind || "Message";
}

function formatStatus(status) {
  return String(status || "open").replaceAll("_", " ");
}

function formatDateTime(value) {
  return formatUsDateTime(value, "Not recorded");
}

function isPreview(id) {
  return String(id || "").startsWith("preview");
}

function searchableText(item) {
  return [
    item.title,
    item.summary,
    item.content,
    item.requesterName,
    item.requesterEmail,
    item.requesterPhone,
    item.owner,
    item.area,
    item.kind,
    item.status,
  ].join(" ").toLowerCase();
}

export default function HelpSupport() {
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("open");
  const [areaFilter, setAreaFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [noteText, setNoteText] = useState("");
  const { dismiss, toast, toasts } = useToast();

  const loadResources = useCallback(async () => {
    const params = new URLSearchParams({ kind: "all", area: "all", limit: "100" });
    return api.get(`/support?${params.toString()}`);
  }, []);

  const { data, error, loading, reload, setData } = useAdminResource(loadResources, fallbackPayload);
  const resources = data.resources || fallbackPayload.resources;
  const metrics = data.metrics || fallbackPayload.metrics;

  const filteredResources = useMemo(() => {
    const term = search.trim().toLowerCase();

    return resources.filter((item) => {
      const typeMatches = typeFilter === "all" || item.kind === typeFilter;
      const areaMatches = areaFilter === "all" || item.area === areaFilter;
      const statusMatches =
        statusFilter === "all" ||
        (statusFilter === "open" && ["open", "in_progress"].includes(item.status)) ||
        item.status === statusFilter;
      const searchMatches = !term || searchableText(item).includes(term);

      return typeMatches && areaMatches && statusMatches && searchMatches;
    });
  }, [areaFilter, resources, search, statusFilter, typeFilter]);

  const selected = filteredResources.find((item) => item._id === selectedId) || filteredResources[0] || resources.find((item) => item._id === selectedId) || null;
  const pagination = usePagination(filteredResources, [typeFilter, statusFilter, areaFilter, search], 8);

  useEffect(() => {
    if (error && !loading) toast(`Could not load support inbox: ${error}`, "error");
  }, [error, loading, toast]);

  useEffect(() => {
    if (!selectedId && filteredResources[0]?._id) {
      setSelectedId(filteredResources[0]._id);
    }
  }, [filteredResources, selectedId]);

  const counts = useMemo(() => ({
    openSupport: resources.filter((item) => item.kind === "ticket" && ["open", "in_progress"].includes(item.status)).length,
    openInquiries: resources.filter((item) => item.kind === "inquiry" && ["open", "in_progress"].includes(item.status)).length,
    resolved: resources.filter((item) => item.status === "resolved").length,
    priority: resources.filter((item) => ["urgent", "high"].includes(item.priority) && ["open", "in_progress"].includes(item.status)).length,
  }), [resources]);

  const replaceResource = (resource) => {
    setData((current) => ({
      ...current,
      resources: (current.resources || []).map((item) => (item._id === resource._id ? resource : item)),
    }));
  };

  const updateResource = async (resource, patch) => {
    if (!resource || isPreview(resource._id)) return;

    try {
      const data = await api.patch(`/support/${resource._id}`, patch);
      replaceResource(data.resource);
      toast("Support message updated", "success");
    } catch (err) {
      toast(err.message, "error");
    }
  };

  const addNote = async (event) => {
    event.preventDefault();
    if (!selected || isPreview(selected._id) || !noteText.trim()) return;

    try {
      const data = await api.post(`/support/${selected._id}/notes`, { text: noteText });
      replaceResource(data.resource);
      setNoteText("");
      toast("Internal note added", "success");
    } catch (err) {
      toast(err.message, "error");
    }
  };

  const filterChips = [
    { label: "Needs action", value: "open", count: counts.openSupport + counts.openInquiries },
    { label: "Support", value: "ticket", type: "kind", count: counts.openSupport },
    { label: "Inquiries", value: "inquiry", type: "kind", count: counts.openInquiries },
    { label: "Resolved", value: "resolved", count: counts.resolved },
  ];

  return (
    <div className="space-y-6 p-6 md:p-10">
      <ToastStack dismiss={dismiss} toasts={toasts} />

      <div className="flex flex-col gap-4 rounded-2xl bg-white p-6 shadow-sm lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-sm font-black uppercase tracking-wide text-[#4391d1]">Help / Support</p>
          <h1 className="mt-2 text-3xl font-black text-gray-900">Support Inbox</h1>
          <p className="mt-2 max-w-3xl text-sm font-medium text-gray-500">
            Manage website contact inquiries and customer support requests from one queue.
          </p>
        </div>
        <WorkflowToolbar actions={[{ label: "Refresh", onClick: reload }]} />
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <InsightCard title="Open Support" value={metrics.openTickets || counts.openSupport} note="Support page requests" tone="warn" />
        <InsightCard title="Contact Inquiries" value={metrics.openInquiries || counts.openInquiries} note="Contact form messages" tone="info" />
        <InsightCard title="Priority Items" value={counts.priority} note="High or urgent open items" tone="warn" />
        <InsightCard title="Resolved" value={counts.resolved} note="Closed support messages" tone="good" />
      </div>

      <section className="rounded-2xl bg-white p-4 shadow-sm">
        <div className="grid gap-3 xl:grid-cols-[1fr_auto_auto]">
          <div className="relative">
            <input
              className="w-full rounded-xl border border-gray-200 px-4 py-3 pl-11 text-sm font-semibold outline-none focus:border-[#4391d1]"
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search customer, email, phone, subject, message..."
              value={search}
            />
            <Search className="absolute left-4 top-3.5 text-gray-400" size={16} />
          </div>
          <select className="rounded-xl border border-gray-200 px-4 py-3 text-sm font-bold" onChange={(event) => setStatusFilter(event.target.value)} value={statusFilter}>
            <option value="all">All Status</option>
            <option value="open">Needs Action</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
            <option value="archived">Archived</option>
          </select>
          <select className="rounded-xl border border-gray-200 px-4 py-3 text-sm font-bold" onChange={(event) => setAreaFilter(event.target.value)} value={areaFilter}>
            {areaOptions.map((item) => <option key={item} value={item}>{item === "all" ? "All Areas" : item}</option>)}
          </select>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {filterChips.map((chip) => {
            const active = chip.type === "kind" ? typeFilter === chip.value : statusFilter === chip.value && typeFilter === "all";

            return (
              <button
                className={`rounded-full px-4 py-2 text-xs font-black transition ${active ? "bg-[#4391d1] text-white" : "border border-gray-200 bg-white text-gray-600 hover:border-[#4391d1] hover:text-[#4391d1]"}`}
                key={`${chip.type || "status"}-${chip.value}`}
                onClick={() => {
                  if (chip.type === "kind") {
                    setTypeFilter(chip.value);
                    setStatusFilter("open");
                  } else {
                    setTypeFilter("all");
                    setStatusFilter(chip.value);
                  }
                }}
                type="button"
              >
                {chip.label} ({chip.count})
              </button>
            );
          })}
          {(typeFilter !== "all" || statusFilter !== "open" || areaFilter !== "all" || search) && (
            <button
              className="rounded-full bg-gray-100 px-4 py-2 text-xs font-black text-gray-600 hover:bg-gray-200"
              onClick={() => {
                setTypeFilter("all");
                setStatusFilter("open");
                setAreaFilter("all");
                setSearch("");
              }}
              type="button"
            >
              Clear
            </button>
          )}
        </div>
      </section>

      <div className="grid gap-6">
        <section className="rounded-2xl bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Headphones className="text-[#4391d1]" />
              <div>
                <h2 className="text-xl font-black text-gray-900">Inbox</h2>
                <p className="text-xs font-bold text-gray-400">{filteredResources.length} message{filteredResources.length === 1 ? "" : "s"} found</p>
              </div>
            </div>
            {loading && <StatusPill tone="info">Loading</StatusPill>}
          </div>

          <div className="mt-5 grid gap-3">
            {!loading && !pagination.paginatedItems.length && (
              <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-8 text-center text-sm font-bold text-gray-500">
                No support messages match the current filters.
              </div>
            )}
            {pagination.paginatedItems.map((resource) => {
              const active = selected?._id === resource._id;
              const Icon = ["ticket", "inquiry"].includes(resource.kind) ? Ticket : BookOpen;

              return (
                <button
                  className={`w-full rounded-2xl border p-4 text-left transition ${active ? "border-[#4391d1] bg-blue-50 shadow-sm" : "border-gray-100 bg-white hover:border-blue-200 hover:bg-blue-50/30"}`}
                  key={resource._id}
                  onClick={() => {
                    setSelectedId(resource._id);
                    setDetailsOpen(true);
                  }}
                  type="button"
                >
                  <div className="flex gap-4">
                    <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${active ? "bg-[#4391d1] text-white" : "bg-blue-50 text-[#4391d1]"}`}>
                      <Icon size={18} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex flex-wrap items-start justify-between gap-2">
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-black text-gray-900">{resource.title}</span>
                          <span className="mt-1 block text-xs font-bold text-gray-500">{resource.requesterName || "Unknown customer"} / {resource.requesterEmail || resource.owner || "No email"}</span>
                        </span>
                        <span className="flex flex-wrap gap-2">
                          <StatusPill tone={priorityTone(resource.priority)}>{resource.priority}</StatusPill>
                          <StatusPill tone={statusTone(resource.status)}>{formatStatus(resource.status)}</StatusPill>
                        </span>
                      </span>
                      <span className="mt-3 line-clamp-2 block text-sm font-semibold leading-6 text-gray-600">{resource.summary || resource.content}</span>
                      <span className="mt-3 flex flex-wrap gap-2 text-xs font-black text-gray-400">
                        <span>{kindLabel(resource.kind)}</span>
                        <span>/</span>
                        <span>{resource.area || "general"}</span>
                        <span>/</span>
                        <span>{formatDateTime(resource.updatedAt)}</span>
                      </span>
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
          <PaginationControls pagination={pagination} />
        </section>
      </div>

      {detailsOpen && selected && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-gray-950/55 p-4 backdrop-blur-sm" role="dialog" aria-modal="true" aria-labelledby="support-message-title">
          <div className="max-h-[92vh] w-full max-w-4xl overflow-hidden rounded-3xl bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-gray-100 p-5">
              <div className="flex min-w-0 items-start gap-3">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-[#4391d1]">
                  {["ticket", "inquiry"].includes(selected.kind) ? <Ticket size={18} /> : <BookOpen size={18} />}
                </span>
                <div className="min-w-0">
                  <p className="text-xs font-black uppercase tracking-wide text-[#4391d1]">{kindLabel(selected.kind)}</p>
                  <h2 id="support-message-title" className="mt-1 truncate text-xl font-black text-gray-900">{selected.title}</h2>
                  <p className="mt-1 text-xs font-bold text-gray-400">Updated {formatDateTime(selected.updatedAt)}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <StatusPill tone={statusTone(selected.status)}>{formatStatus(selected.status)}</StatusPill>
                <button className="flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 text-gray-500 hover:border-[#4391d1] hover:text-[#4391d1]" onClick={() => setDetailsOpen(false)} type="button" aria-label="Close message details">
                  <X size={18} />
                </button>
              </div>
            </div>

            <div className="max-h-[calc(92vh-86px)] overflow-y-auto p-5">
              <div className="grid gap-5 lg:grid-cols-[1fr_320px]">
                <div className="space-y-5">
                  <section className="rounded-2xl bg-gray-50 p-5">
                    <p className="text-xs font-black uppercase tracking-wide text-gray-400">Message</p>
                    <p className="mt-3 whitespace-pre-line text-sm font-semibold leading-7 text-gray-700">{selected.content || selected.summary}</p>
                  </section>

                  <form className="grid gap-3 rounded-2xl border border-gray-100 p-5" onSubmit={addNote}>
                    <p className="text-xs font-black uppercase tracking-wide text-gray-400">Internal Notes</p>
                    <textarea className="min-h-24 rounded-xl border border-gray-200 px-4 py-3 text-sm font-semibold" onChange={(event) => setNoteText(event.target.value)} placeholder="Add private admin note" value={noteText} />
                    <button className="inline-flex w-fit items-center justify-center gap-2 rounded-xl bg-[#4391d1] px-4 py-3 text-sm font-black text-white hover:bg-blue-600 disabled:bg-gray-300" disabled={isPreview(selected._id)} type="submit">
                      <MessageSquarePlus size={16} /> Add Note
                    </button>
                    <div className="grid gap-2">
                      {(selected.notes || []).map((note, index) => (
                        <p className="rounded-xl bg-gray-50 p-3 text-sm font-semibold text-gray-600" key={`${note.text}-${index}`}>
                          {note.text}
                        </p>
                      ))}
                      {!selected.notes?.length && <p className="text-sm font-bold text-gray-400">No internal notes yet.</p>}
                    </div>
                  </form>
                </div>

                <aside className="space-y-5">
                  <section className="grid gap-3 rounded-2xl border border-gray-100 p-5">
                    <p className="text-xs font-black uppercase tracking-wide text-gray-400">Customer</p>
                    <p className="font-black text-gray-900">{selected.requesterName || "Not provided"}</p>
                    {selected.requesterEmail && (
                      <a className="inline-flex items-center gap-2 rounded-xl bg-blue-50 px-3 py-2 text-sm font-bold text-[#4391d1]" href={`mailto:${selected.requesterEmail}`}>
                        <Mail size={15} /> {selected.requesterEmail}
                      </a>
                    )}
                    {selected.requesterPhone && (
                      <a className="inline-flex items-center gap-2 rounded-xl bg-blue-50 px-3 py-2 text-sm font-bold text-[#4391d1]" href={`tel:${selected.requesterPhone}`}>
                        <Phone size={15} /> {selected.requesterPhone}
                      </a>
                    )}
                  </section>

                  <section className="grid gap-3 rounded-2xl border border-gray-100 p-5">
                    <p className="text-xs font-black uppercase tracking-wide text-gray-400">Manage</p>
                    <label>
                      <span className="mb-2 block text-xs font-black uppercase text-gray-500">Status</span>
                      <select className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 text-sm font-bold" disabled={isPreview(selected._id)} onChange={(event) => updateResource(selected, { status: event.target.value })} value={selected.status}>
                        {statusOptions.map((status) => <option key={status} value={status}>{formatStatus(status)}</option>)}
                      </select>
                    </label>
                    <label>
                      <span className="mb-2 block text-xs font-black uppercase text-gray-500">Priority</span>
                      <select className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 text-sm font-bold" disabled={isPreview(selected._id)} onChange={(event) => updateResource(selected, { priority: event.target.value })} value={selected.priority || "medium"}>
                        {priorityOptions.map((priority) => <option key={priority} value={priority}>{priority}</option>)}
                      </select>
                    </label>
                    {selected.status !== "resolved" && (
                      <button className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-3 text-sm font-black text-white disabled:opacity-50" disabled={isPreview(selected._id)} onClick={() => updateResource(selected, { status: "resolved" })} type="button">
                        <CheckCircle2 size={16} /> Mark Resolved
                      </button>
                    )}
                  </section>
                </aside>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
